// ** Redux Imports
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { isNil } from 'lodash-es';

import { RootState } from '..';

import { ConfiguratorConfigRoot, GamificationItem, GamificationTier } from '@/components/modules/spark/configurator/types';
import { getErrorMessage } from '@/utils/errors';

const DEBUG = false;

type GenericError = {
  rejectValue: {
    error: string;
  };
};

const apiUrl = window.config.publicApiUrl;

type ConfiguratorConfigProps = undefined;

type ConfiguratorConfigResult = {
  data: ConfiguratorConfigRoot;
  success: boolean;
};

export const fetchConfiguratorConfig = createAsyncThunk<ConfiguratorConfigResult, ConfiguratorConfigProps, GenericError>(
  'configuratorConfig/fetch',
  async (_args, { getState, rejectWithValue }) => {
    try {
      // Pass the owner's access token when signed in so the API can enrich the config with owner-scoped
      // data; anonymous configurator sessions (no token) still get the public config. Raw token, no Bearer
      // prefix, matching the other owner-hub calls (e.g. ownerHubStats, casinoDomainInfo).
      const authToken = (getState() as RootState).authentication.access_token;
      const response = await axios.get<ConfiguratorConfigRoot>(
        `${apiUrl}/configurator`,
        authToken ? { headers: { Authorization: authToken } } : undefined,
      );

      if (!isNil(response.data)) {
        return { data: response.data, success: true };
      }

      return rejectWithValue({
        error: "Couldn't get configurator config",
      });
    } catch (error) {
      return rejectWithValue({
        error: getErrorMessage(error),
      });
    }
  },
);

/** Catalog gamification item with its tier ladder attached. */
export interface GamificationItemWithTiers extends GamificationItem {
  tiers: GamificationTier[];
}

/** Join `gamification_items` with their `gamification_tiers` (by `gamification_item_id`), both priority-sorted. */
const groupGamificationItems = (config: ConfiguratorConfigRoot): GamificationItemWithTiers[] =>
  config.gamification_items
    .toSorted((a, b) => a.priority - b.priority)
    .map((item) => ({
      ...item,
      tiers: config.gamification_tiers
        .filter((tier) => tier.gamification_item_id === item.id)
        .toSorted((a, b) => a.priority - b.priority),
    }));

export interface ConfiguratorConfigReducer {
  config: ConfiguratorConfigRoot | null;
  /** `config.gamification_items` joined with their `config.gamification_tiers`. */
  gamificationItems: GamificationItemWithTiers[];
  loaded: boolean;
  loading: boolean;
  error: any;
  /** RTK requestId of the latest in-flight fetch. fulfilled/rejected apply only when they match, and the
   *  teardown reset clears it, so a fetch started under a previous owner cannot repopulate after a reset. */
  currentRequestId?: string;
}

export const configuratorConfigSlice = createSlice({
  name: 'configuratorConfig',
  initialState: <ConfiguratorConfigReducer>{
    config: null,
    gamificationItems: [],
    loaded: false,
    loading: false,
    error: null,
    currentRequestId: undefined,
  },
  reducers: {
    // The config is now OWNER-SCOPED (enriched with the signed-in owner's token), so it must not outlive
    // the session on a shared browser: the logout + identity-change teardown listeners (store/index.ts)
    // dispatch this so the next owner refetches under their own token (every consumer gates on `!config`).
    resetConfiguratorConfig: (state) => {
      state.config = null;
      state.gamificationItems = [];
      state.loaded = false;
      state.loading = false;
      state.error = null;
      // Drop the in-flight request id so a fetch that started under the previous owner, if it lands after
      // this reset, fails the fulfilled/rejected guard instead of repopulating the slice for the next owner.
      state.currentRequestId = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConfiguratorConfig.fulfilled, (state, action) => {
        // Ignore a response from a superseded request: a fetch in flight at a logout / identity swap (the
        // teardown reset cleared currentRequestId), or an older overlapping fetch during a token rotation.
        if (action.meta.requestId !== state.currentRequestId) return;
        state.loaded = true;
        state.loading = false;
        if (action.payload.success) {
          state.config = action.payload.data;
          state.gamificationItems = groupGamificationItems(action.payload.data);
        }
        DEBUG && console.timeEnd('fetchConfiguratorConfig.query');
        DEBUG && console.log('fetchConfiguratorConfig.fulfilled', action.payload);
      })
      .addCase(fetchConfiguratorConfig.pending, (state, action) => {
        state.currentRequestId = action.meta.requestId;
        state.loaded = false;
        state.loading = true;
        DEBUG && console.time('fetchConfiguratorConfig.query');
      })
      .addCase(fetchConfiguratorConfig.rejected, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.loaded = false;
        state.loading = false;
        // Always leave an error behind (a rejection without a payload carries none): readers treat "not loaded and
        // no error" as still loading, so an empty error here made them wait forever.
        state.error = action.payload?.error ?? action.error?.message ?? 'Configurator request failed';
        DEBUG && console.log('fetchConfiguratorConfig.rejected', action.payload);
      });
  },
});

export const { resetConfiguratorConfig } = configuratorConfigSlice.actions;

export default configuratorConfigSlice.reducer;
